/**
 * ATTIFIT — URL-driven collection pinning.
 *
 * A personalized landing URL like
 *   /collections/<collection>?handles=<handle-1>,<handle-2>
 * shows those products first on page 1 of the collection, each rendered
 * server-side through sections/attifit-product-card.liquid so pinned cards
 * are identical to native Symmetry cards. Their normal grid positions are
 * hidden so nothing appears twice.
 *
 * Behavioral contract (see ATTIFIT-CUSTOMIZATIONS.md):
 * - Two accepted URL formats resolve to one pin list:
 *     ?handles=a,b            canonical (all new links/ads)
 *     ?sort_by=a,b            legacy adapter (pre-handles Meta ads used
 *                             the Bluvap/Tacday convention; the value is
 *                             treated as the pin list ONLY when it is not
 *                             a genuine Shopify sort value, exactly like
 *                             the Bluvap reference's is_pinning_sort)
 *   A GENUINE native sort (?sort_by=best-selling etc.) always means real
 *   sorting: it disables pinning even if handles is also present. Legacy
 *   URLs are interpreted in place - never redirected or rewritten - so ad
 *   destinations load once and keep their visible URL.
 * - Pins apply only to the initial landing state: page 1, no genuine
 *   sort_by, no filter.* params, no search q. Any filter/sort/pagination
 *   interaction disables pinning - and invalidates/aborts in-flight pin
 *   fetches IMMEDIATELY, before the theme's debounced AJAX changes the
 *   URL - and the pin value (either format) is stripped from the URLs
 *   those interactions produce, while a destination's own genuine
 *   sort_by is never touched. Attribution params (UTMs etc.) are
 *   preserved: hidden inputs mirror them into the filter form, because
 *   the theme rebuilds filtered URLs from form inputs alone.
 * - Ordered placeholders reserve the pinned rows up front (no late layout
 *   shift); each is replaced by its card as that fetch settles, URL order
 *   preserved; invalid/unpublished handles remove their placeholder.
 * - Only the first EAGER_PINNED_IMAGES cards get eager images; later pins
 *   stay lazy.
 * - Card fetches use the locale-aware storefront root
 *   (window.Shopify.routes.root), so Shopify Markets subfolders work.
 * - Browser Back to the pinned URL re-applies pins (the native AJAX
 *   replacement produces a fresh grid; the MutationObserver re-evaluates).
 * - Races die by state token + AbortController + per-grid attempt marker.
 * - Hard cap of 8 handles (the Bluvap reference had no explicit cap - its
 *   Liquid all_products design implied 20; this build fetches per handle).
 * - Native product count and pagination are never modified.
 *
 * Loaded only by sections/main-collection.liquid.
 */
(function () {
  'use strict';

  if (window.attifitCollectionPinningInit) return; // theme editor can re-run section scripts
  window.attifitCollectionPinningInit = true;

  var MAX_PINNED_HANDLES = 8;
  var EAGER_PINNED_IMAGES = 4; // one desktop row; the rest stay lazy
  var HANDLE_SHAPE = /^[a-z0-9][a-z0-9-]*$/;
  var PIN_PARAM = 'handles';
  var LINK_SELECTOR = '.link-dropdown__link, .pagination a, .filter-group__applied-item, .filter-group__clear-link';
  // Params rebuilt or deliberately dropped by collection interactions;
  // everything else (utm_*, fbclid, gclid, ...) is preserved.
  var PRESERVE_EXCLUDE = [PIN_PARAM, 'page', 'sort_by', 'q'];

  var stateToken = 0;
  var abortController = null;
  var observer = null;
  var observedListing = null;
  var scheduled = false;

  function getSectionEl() {
    var fc = document.querySelector('filter-container');
    return fc ? (fc.closest('.shopify-section') || document.body) : null;
  }

  function getListing(sectionEl) {
    return sectionEl ? sectionEl.querySelector('.filters-adjacent.collection-listing') : null;
  }

  function getGrid(sectionEl) {
    var listing = getListing(sectionEl);
    return listing ? listing.querySelector('.product-grid') : null;
  }

  function shopifyRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  // Genuine Shopify sort values. The script tag (rendered by
  // main-collection.liquid) carries the collection's real sort_options in
  // data-attifit-sort-values; the standard set stays UNIONED in as a
  // resilience floor, so a real sort can never be misread as a pin list
  // even if the attribute is ever missing or partial.
  var NATIVE_SORT_VALUES = ['manual', 'best-selling', 'title-ascending',
    'title-descending', 'price-ascending', 'price-descending',
    'created-ascending', 'created-descending'];

  function isNativeSort(value) {
    if (NATIVE_SORT_VALUES.indexOf(value) !== -1) return true;
    var el = document.querySelector('[data-attifit-sort-values]');
    if (!el) return false;
    var attr = el.getAttribute('data-attifit-sort-values') || '';
    return attr.split(',').indexOf(value) !== -1;
  }

  // Decide where (if anywhere) this URL's pin list comes from:
  //   { source: 'handles',        raw: 'a,b' }  canonical ?handles=
  //   { source: 'legacy-sort-by', raw: 'a,b' }  pre-handles ad format
  //   null                                       no pinning
  // Precedence: a genuine native sort_by is real sorting and returns null
  // even when handles is present (explicit customer sorting beats the
  // personalized landing state); otherwise handles wins; otherwise a
  // non-native sort_by value IS the legacy pin list (only ATTIFIT's old
  // ad convention produces one - Shopify itself falls back to default
  // ordering for it). Page 2+, q and filter.* disable every source.
  function resolvePinSource(params) {
    if (parseInt(params.get('page') || '1', 10) > 1) return null;
    if (params.get('q')) return null;
    var keys = Array.from(params.keys());
    for (var i = 0; i < keys.length; i += 1) {
      if (keys[i].indexOf('filter.') === 0) return null;
    }
    var sortBy = params.get('sort_by');
    if (sortBy && isNativeSort(sortBy)) return null;
    var handles = params.get(PIN_PARAM);
    if (handles) return { source: 'handles', raw: handles };
    if (sortBy) return { source: 'legacy-sort-by', raw: sortBy };
    return null;
  }

  // Sanitization semantics follow the tested Bluvap implementation:
  // trim + lowercase, drop empties (repeated commas, stray spaces),
  // validate the handle shape, dedupe preserving first occurrence.
  function parseHandles(raw) {
    var seen = Object.create(null);
    var out = [];
    String(raw).split(',').forEach(function (part) {
      var handle = part.trim().toLowerCase();
      if (!handle || !HANDLE_SHAPE.test(handle) || seen[handle]) return;
      seen[handle] = true;
      out.push(handle);
    });
    return out.slice(0, MAX_PINNED_HANDLES);
  }

  // One normalization for every collection interaction link (sort,
  // pagination, applied-filter removal, Clear All):
  // - the pin parameter is removed;
  // - the destination's OWN semantics are untouched (its page, sort_by and
  //   filter.* values are what that link intends - none are re-added when
  //   the destination deliberately dropped them, none are overwritten);
  // - current attribution/unrelated params (utm_*, fbclid, gclid, ...) are
  //   merged in when absent. Clear All is rendered from bare
  //   collection.url and the theme's sort links are rebuilt from the
  //   location, so without this merge those clicks dropped attribution.
  function normalizeInteractionLink(a) {
    try {
      var url = new URL(a.href, window.location.origin);
      var before = url.pathname + url.search;
      url.searchParams.delete(PIN_PARAM);
      // A legacy pin value riding in the destination's sort_by (theme
      // pagination links rebuild the current query string) is a pin
      // parameter too and comes out the same way. A GENUINE native sort
      // value is that link's own semantics (the sort dropdown) and is
      // never touched.
      var destSort = url.searchParams.get('sort_by');
      if (destSort && !isNativeSort(destSort)) url.searchParams.delete('sort_by');
      var current = new URLSearchParams(window.location.search);
      current.forEach(function (value, key) {
        if (PRESERVE_EXCLUDE.indexOf(key) !== -1) return;
        if (key.indexOf('filter.') === 0) return;
        if (url.searchParams.has(key)) return; // never overwrite the destination's own value
        url.searchParams.append(key, value);
      });
      var query = url.searchParams.toString();
      var after = url.pathname + (query ? '?' + query : '');
      if (after !== before) a.href = after + url.hash;
    } catch (e) {
      // leave the link alone
    }
  }

  function sanitizeLinks(sectionEl) {
    if (!sectionEl) return;
    sectionEl.querySelectorAll(LINK_SELECTOR).forEach(normalizeInteractionLink);
  }

  // The theme's FilterContainer rebuilds filtered URLs exclusively from the
  // filter form's inputs, which would drop attribution params (utm_*,
  // fbclid, gclid, ...) on the first filter change. Mirror every unrelated
  // current param into the form as hidden inputs so native URL building
  // carries them - while the pin parameter and collection-control params
  // stay deliberately excluded.
  function syncPreservedParams(sectionEl) {
    if (!sectionEl) return;
    var form = sectionEl.querySelector('#CollectionFilterForm');
    if (!form) return;
    form.querySelectorAll('input[data-attifit-preserved]').forEach(function (el) { el.remove(); });
    var params = new URLSearchParams(window.location.search);
    params.forEach(function (value, key) {
      if (PRESERVE_EXCLUDE.indexOf(key) !== -1) return;
      if (key.indexOf('filter.') === 0) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      input.setAttribute('data-attifit-preserved', '');
      form.appendChild(input);
    });
  }

  function removePlaceholders() {
    document.querySelectorAll('.attifit-pin-placeholder').forEach(function (el) { el.remove(); });
  }

  function buildPlaceholder() {
    var ph = document.createElement('div');
    ph.className = 'attifit-pin-placeholder';
    ph.setAttribute('data-attifit-pinned', '');
    ph.setAttribute('aria-hidden', 'true');
    ph.innerHTML = '<div class="attifit-pin-placeholder__media"></div>'
      + '<div class="attifit-pin-placeholder__line"></div>'
      + '<div class="attifit-pin-placeholder__line attifit-pin-placeholder__line--short"></div>';
    return ph;
  }

  // Immediate, COMPLETE invalidation: abort in-flight pin fetches, drop
  // reserved placeholders, remove already-inserted pinned cards and unhide
  // their natural duplicates - the moment a filter/sort/pagination/history
  // interaction begins (the theme debounces its AJAX by ~700ms, during
  // which the URL still looks pin-eligible, and if that AJAX ever fails the
  // page must already be back in an ordinary, usable unpinned state).
  function invalidate() {
    stateToken += 1;
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    removePlaceholders();
    document.querySelectorAll('.attifit-pinned-card').forEach(function (el) { el.remove(); });
    document.querySelectorAll('[data-attifit-dup-hidden]').forEach(function (el) {
      el.hidden = false;
      el.removeAttribute('data-attifit-dup-hidden');
    });
  }

  // The current URL's pin-run identity (source + raw value) when pinning
  // is eligible right now, else null. Source is part of the identity so
  // ?handles=a,b and ?sort_by=a,b are two DIFFERENT URL states to the
  // per-grid attempt marker and the per-card stale checks. Re-checked
  // before every placeholder replacement: a history jump (Back to pins,
  // immediate Forward to a filtered URL) changes the URL without any
  // click or form event, and a card settling in that window must not
  // enter the old grid.
  function pinRunId(source) {
    return source ? source.source + '|' + source.raw : null;
  }

  function currentPinRunId() {
    return pinRunId(resolvePinSource(new URLSearchParams(window.location.search)));
  }

  function fetchCard(handle, signal) {
    var url = shopifyRoot() + 'products/' + encodeURIComponent(handle) + '?section_id=attifit-product-card';
    return fetch(url, { signal: signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var card = tpl.content.querySelector('product-block[data-product-id]');
        if (!card) throw new Error('No product card markup for "' + handle + '"');
        return card;
      });
  }

  function hideNaturalCard(grid, productId) {
    grid.querySelectorAll(':scope > product-block[data-product-id="' + productId + '"]:not([data-attifit-pinned])').forEach(function (dupe) {
      dupe.hidden = true;
      dupe.setAttribute('data-attifit-dup-hidden', '');
    });
  }

  function startPinning(grid, source) {
    invalidate(); // one active pin run at a time
    var token = stateToken;
    var runId = pinRunId(source);

    // Attempt marker: this grid instance + this pin state (source AND raw
    // value) run once - even if every fetch fails and the placeholders
    // disappear again (an all-invalid URL must not retry forever off its
    // own mutations). A native AJAX replacement produces a fresh grid,
    // which re-attempts.
    grid.dataset.attifitPinAttempt = runId;

    var handles = parseHandles(source.raw);
    if (!handles.length) return;

    abortController = 'AbortController' in window ? new AbortController() : null;
    var signal = abortController ? abortController.signal : undefined;

    // Reserve the pinned rows up front, in URL order.
    var placeholders = handles.map(function () { return buildPlaceholder(); });
    var frag = document.createDocumentFragment();
    placeholders.forEach(function (ph) { frag.appendChild(ph); });
    grid.prepend(frag);

    handles.forEach(function (handle, index) {
      fetchCard(handle, signal)
        .then(function (card) {
          if (token !== stateToken) return; // a newer state won meanwhile
          if (currentPinRunId() !== runId) return; // URL moved on (history jump)
          var ph = placeholders[index];
          if (!ph.isConnected) return; // grid replaced or run invalidated
          card.setAttribute('data-attifit-pinned', '');
          card.classList.add('attifit-pinned-card');
          if (index < EAGER_PINNED_IMAGES) {
            card.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
              img.setAttribute('loading', 'eager');
            });
          }
          ph.replaceWith(card);
          if (card.dataset.productId) hideNaturalCard(grid, card.dataset.productId);
        })
        .catch(function () {
          if (token !== stateToken) return;
          var ph = placeholders[index];
          if (ph.isConnected) ph.remove(); // invalid handle: give the space back
        });
    });
  }

  function evaluate() {
    var sectionEl = getSectionEl();
    if (!sectionEl) return;

    // Always keep interaction links clean and attribution params mirrored,
    // even when pinning is inactive.
    sanitizeLinks(sectionEl);
    syncPreservedParams(sectionEl);

    var grid = getGrid(sectionEl);
    if (!grid) return;

    var params = new URLSearchParams(window.location.search);
    var source = resolvePinSource(params);
    if (!source) return;

    if (grid.dataset.attifitPinAttempt === pinRunId(source)) return; // this grid already ran for this URL state
    startPinning(grid, source);
  }

  function scheduleEvaluate() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      evaluate();
    }, 50);
  }

  // The theme's AJAX filtering swaps [data-ajax-container] innerHTML with no
  // completion event; observing the listing container catches every
  // replacement (filter, sort, pagination and popstate) in one place. Our
  // own placeholder/card mutations also fire it - the per-grid attempt
  // marker makes those passes no-ops.
  function bindObserver() {
    var listing = getListing(getSectionEl());
    if (!listing || listing === observedListing || !('MutationObserver' in window)) return;
    if (observer) observer.disconnect();
    observedListing = listing;
    observer = new MutationObserver(scheduleEvaluate);
    observer.observe(listing, { childList: true, subtree: true });
  }

  // In the theme editor a section reload replaces the whole section DOM.
  document.addEventListener('shopify:section:load', function (evt) {
    if (evt.target && evt.target.querySelector && evt.target.querySelector('filter-container')) {
      observedListing = null;
      bindObserver();
      scheduleEvaluate();
    }
  });

  // A filter interaction invalidates in-flight pins IMMEDIATELY - the
  // theme's debounced AJAX will not change the URL for ~700ms, and settled
  // pin batches must not flash into a grid that is about to be replaced.
  function filterInteractionInvalidate(evt) {
    var t = evt.target;
    if (!t || !t.closest || !t.closest('#CollectionFilterForm')) return;
    invalidate();
  }
  document.addEventListener('change', filterInteractionInvalidate, true);
  document.addEventListener('submit', filterInteractionInvalidate, true);

  // History navigation changes the URL with no click or form event at all.
  // Invalidate ONLY - never evaluate here: the URL flips instantly while
  // the OLD grid is still on screen, and an eager evaluate would create
  // placeholders and start card fetches against that stale grid (leaving
  // pins on a filtered grid if the native replacement then failed). The
  // native popstate AJAX replaces the listing; the MutationObserver sees
  // the FRESH grid and evaluate() re-pins only that (per-card URL checks
  // still apply). If the replacement fails, the old grid stays clean and
  // unpinned.
  window.addEventListener('popstate', function () {
    invalidate();
  });

  // Belt-and-braces: normalize interaction links again at click time, in
  // the capture phase - before FilterContainer's own delegated handler
  // reads el.href for fetch + pushState. These clicks also start a
  // collection update, so invalidate immediately too.
  document.addEventListener('click', function (evt) {
    if (!evt.target.closest) return;
    var a = evt.target.closest('a');
    if (!a || !a.matches || !a.matches(LINK_SELECTOR)) return;
    var sectionEl = getSectionEl();
    if (!sectionEl || !sectionEl.contains(a)) return;
    normalizeInteractionLink(a);
    invalidate();
  }, true);

  bindObserver();
  evaluate();
})();
